// @ts-ignore
import { post } from '@mindtickle/api-helpers';

import { CALL_AI_COMPANY_SETTINGS_QUERY, CALL_AI_USER_DATA_QUERY } from './callaiQueries';

type CallAiFeatureFlag = {
  name: string;
  isEnabledForTenant: boolean;
  active: boolean;
};

type CallAiTenant = {
  id: string;
  name: string;
};

type CallAiCompanySettingsData = {
  getFeatureFlags?: CallAiFeatureFlag[];
  getTenantByMtCname?: CallAiTenant | null;
};

type CallAiCompanySettingsResponse = {
  data?: CallAiCompanySettingsData;
  errors?: unknown[];
};

type CallAiUserDataResponse = {
  data?: unknown;
  errors?: unknown[];
  companySettings?: CallAiCompanySettingsData;
};

const COMPANY_SETTINGS_CACHE_OPTIONS =
  'ttl=604800000; type=local; cacheId=CALL_AI_COMPANY_SETTINGS; strategy=BACKGROUND_FETCH';

const USER_DATA_CACHE_OPTIONS =
  'ttl=604800000; type=local; cacheId=CALL_AI_USER_DATA; strategy=BACKGROUND_FETCH';

/**
 * Fetches CallAI company settings (including feature flags and tenant info)
 * using the CallAI GraphQL endpoint, following the same pattern as the
 * legacy CallAI Wrapper API.
 *
 * NOTE:
 * - Expects `auth` to contain `sessionId` and `company.id`.
 */
export const fetchCallAiCompanySettings = async (
  sessionId: string,
  companyId: string
): Promise<CallAiCompanySettingsResponse> => {
  const defaultResponse: CallAiCompanySettingsResponse = {};

  try {
    const { data, errors } = await post(
      {
        url: `${process.env.CALL_AI_API_BASE_URL}/graphapi`,
      },
      {
        headers: {
          'x-token': sessionId,
        },
        body: {
          query: CALL_AI_COMPANY_SETTINGS_QUERY,
          variables: {
            cname: companyId,
          },
        },
        cacheOptions: COMPANY_SETTINGS_CACHE_OPTIONS,
      }
    );

    return { data, errors };
  } catch (errors) {
    // eslint-disable-next-line no-console
    console.error(errors);
    return defaultResponse;
  }
};

/**
 * Fetches CallAI user data by first extracting company settings
 * (to get the tenant id) and then calling the CallAI Apollo Server
 * GraphQL endpoint with that id.
 *
 * The tenant id from `getTenantByMtCname.id` is passed as the `domain`
 * variable to the `GetUserData` query.
 */
export const fetchCallAiUserData = async (
  sessionId: string,
  companyId: string
): Promise<CallAiUserDataResponse> => {
  const companySettingsResponse = await fetchCallAiCompanySettings(sessionId, companyId);
  const tenantId = companySettingsResponse.data?.getTenantByMtCname?.id;

  if (!tenantId) {
    return { data: null, errors: ['Missing tenant id from getTenantByMtCname'] };
  }

  const { data, errors } = await post(
    {
      url: `${process.env.CALL_AI_API_BASE_URL}/graphapi`,
    },
    {
      headers: {
        'x-token': sessionId,
      },
      body: {
        query: CALL_AI_USER_DATA_QUERY,
        variables: {
          domain: tenantId,
          isEmbeddedNative: false,
          shouldSkipBrandColor: true,
        },
      },
      cacheOptions: USER_DATA_CACHE_OPTIONS,
    }
  );

  return {
    data,
    companySettings: companySettingsResponse.data,
    errors,
  };
};
